import { useCallback, useMemo, useRef, useState } from "react";
import type { ModoAtendimento } from "@/lib/atendimentoMode";
import { toast } from "@/lib/appToast";
import { cardapioT, type CardapioI18nKey, type CardapioLocale } from "@/lib/cardapio/i18n";
import type { CardapioCatalogo } from "@/lib/cardapio/types";
import type { CartItem } from "@/lib/cardapio/types";
import {
  collectTranslationItems,
  filterItemsNeedingTranslation,
  loadSessionTranslationCache,
  mergeSessionTranslationCache,
  resolveCartItemDescricao,
  resolveCartItemNome,
  resolveDynamicText,
  resolveModalItemTexts,
  translateCatalogItems,
  type DynamicTranslationMap,
} from "@/lib/cardapio/translation";

export function useCardapioLocale(catalogo: CardapioCatalogo | null, modoAtendimento: ModoAtendimento = "mesa") {
  const [locale, setLocale] = useState<CardapioLocale>("pt");
  const [dynamicMap, setDynamicMap] = useState<DynamicTranslationMap>({});
  const [translating, setTranslating] = useState(false);
  const sessionCacheRef = useRef(loadSessionTranslationCache());
  const translationRunRef = useRef(0);

  const t = useCallback((key: CardapioI18nKey) => cardapioT(locale, key, modoAtendimento), [locale, modoAtendimento]);

  const td = useCallback(
    (entityType: string, entityId: string, field: string, original: string) =>
      resolveDynamicText(locale, dynamicMap, entityType, entityId, field, original),
    [locale, dynamicMap],
  );

  const empresaNomeDisplay = useMemo(() => {
    const original = catalogo?.empresa.nome ?? "";
    return td("empresa", "0", "nome", original);
  }, [catalogo?.empresa.nome, td]);

  const resolveCartNome = useCallback(
    (item: CartItem) => resolveCartItemNome(catalogo, locale, dynamicMap, item),
    [catalogo, locale, dynamicMap],
  );

  const resolveCartDescricao = useCallback(
    (item: CartItem) => resolveCartItemDescricao(catalogo, locale, dynamicMap, item),
    [catalogo, locale, dynamicMap],
  );

  const resolveModalTexts = useCallback(
    (item: { produto_id: number; nome: string; descricao: string | null }) =>
      resolveModalItemTexts(catalogo, locale, dynamicMap, item),
    [catalogo, locale, dynamicMap],
  );

  const selectLocale = useCallback(
    async (next: CardapioLocale) => {
      if (next === locale) return;

      if (next === "pt") {
        translationRunRef.current += 1;
        setLocale("pt");
        setTranslating(false);
        return;
      }

      if (!catalogo || translating) return;

      const cached = sessionCacheRef.current[next] ?? {};
      const allItems = collectTranslationItems(catalogo);
      const missingItems = filterItemsNeedingTranslation(allItems, cached);

      setLocale(next);

      if (missingItems.length === 0) {
        setDynamicMap(cached);
        return;
      }

      const runId = translationRunRef.current + 1;
      translationRunRef.current = runId;
      setDynamicMap(cached);
      setTranslating(true);

      try {
        const result = await translateCatalogItems({
          target: next,
          items: missingItems,
          initialMap: cached,
          onBatchComplete: (_batchTranslations, accumulated) => {
            if (translationRunRef.current !== runId) return;
            setDynamicMap(accumulated);
            sessionCacheRef.current[next] = accumulated;
            mergeSessionTranslationCache(next, accumulated);
          },
        });

        if (translationRunRef.current !== runId) return;

        sessionCacheRef.current[next] = result.translations;
        setDynamicMap(result.translations);

        if (result.failedItemCount > 0) {
          toast.error(cardapioT(next, "errors.translationPartial"));
        }
      } catch {
        if (translationRunRef.current === runId) {
          toast.error(cardapioT(next, "errors.translationPartial"));
        }
      } finally {
        if (translationRunRef.current === runId) {
          setTranslating(false);
        }
      }
    },
    [catalogo, locale, translating],
  );

  return {
    locale,
    translating,
    selectLocale,
    t,
    td,
    empresaNomeDisplay,
    resolveCartNome,
    resolveCartDescricao,
    resolveModalTexts,
  };
}
